import axios from 'axios';

export const CASHBOOK_STATEMENT_PAGE_SIZE = 500;

export const isCreditEntryType = (entryType) =>
    ['INCOME', 'TRANSFER_IN'].includes(String(entryType || '').toUpperCase());

export const isDebitEntryType = (entryType) =>
    ['EXPENSE', 'TRANSFER_OUT'].includes(String(entryType || '').toUpperCase());

export const formatMoney = (value) => {
    return new Intl.NumberFormat('en-KE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(value || 0));
};

export const escapeCsvValue = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    return `"${text.replace(/"/g, '""')}"`;
};

export const buildCashbookStatementRows = (entries, openingBalance) => {
    let runningBalance = Number(openingBalance || 0);

    return [...entries]
        .sort((left, right) => {
            const leftTime = new Date(left.entry_date || 0).getTime();
            const rightTime = new Date(right.entry_date || 0).getTime();

            if (leftTime !== rightTime) {
                return leftTime - rightTime;
            }

            return Number(left.id || 0) - Number(right.id || 0);
        })
        .map((entry) => {
            const amount = Number(entry.amount || 0);
            const isCredit = isCreditEntryType(entry.entry_type);
            const isDebit = isDebitEntryType(entry.entry_type);
            const entryDate = String(entry.entry_date || '').slice(0, 10);
            const referenceDisplay = String(entry.payment_reference || entry.reference_id || entry.reference_type || '').trim() || '-';
            const detailsDisplay = String(entry.description || '-').trim() || '-';

            if (isCredit) {
                runningBalance += amount;
            } else if (isDebit) {
                runningBalance -= amount;
            }

            return {
                ...entry,
                entry_date_value: entryDate,
                reference_display: referenceDisplay,
                details_display: detailsDisplay,
                debit: isDebit ? amount : 0,
                credit: isCredit ? amount : 0,
                running_balance: runningBalance,
                status_display: entry.reconciled ? 'Reconciled' : 'Pending',
                search_text: [
                    entry.description,
                    entry.payment_reference,
                    entry.reference_id,
                    entry.reference_type,
                    entry.entry_type,
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase(),
            };
        });
};

export const buildCashbookStatementCsv = ({ account, openingBalance, closingBalance, dateRangeLabel, rows }) => {
    const headerLines = [
        ['Account', account?.account_name || ''],
        ['Account Type', account?.account_type || ''],
        ['Opening Balance', formatMoney(openingBalance)],
        ['Closing Balance', formatMoney(closingBalance)],
        ['Statement Range', dateRangeLabel || 'All dates'],
        [],
        ['Date', 'Reference', 'Details', 'Debit', 'Credit', 'Running Balance', 'Status'],
    ];

    const bodyLines = rows.map((row) => [
        row.entry_date_value || '',
        row.reference_display || '',
        row.details_display || '',
        row.debit ? formatMoney(row.debit) : '',
        row.credit ? formatMoney(row.credit) : '',
        formatMoney(row.running_balance),
        row.status_display || '',
    ]);

    return [...headerLines, ...bodyLines]
        .map((line) => line.map(escapeCsvValue).join(','))
        .join('\n');
};

export const fetchAllCashbookEntries = async ({ baseUrl, headers, accountId }) => {
    const allRows = [];
    const pageSize = CASHBOOK_STATEMENT_PAGE_SIZE;
    let offset = 0;
    let total = null;

    while (total === null || allRows.length < total) {
        const response = await axios.get(`${baseUrl}/api/cashbook/entries`, {
            headers,
            params: {
                account_id: accountId,
                limit: pageSize,
                offset,
            },
        });

        if (response.data?.success === false) {
            throw new Error(response.data?.message || 'Failed to load cashbook entries.');
        }

        const pageRows = Array.isArray(response.data?.data) ? response.data.data : [];
        total = Number(response.data?.total || 0);
        allRows.push(...pageRows);

        if (pageRows.length < pageSize) {
            break;
        }

        offset += pageSize;
    }

    return allRows;
};
